import { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { works, workTypes } from "@/mocks/works";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const yearRanges = [
  { label: "全部年份", min: 0, max: 9999 },
  { label: "2023 - 2024", min: 2023, max: 2024 },
  { label: "2021 - 2022", min: 2021, max: 2022 },
  { label: "2019 - 2020", min: 2019, max: 2020 },
  { label: "2018 及更早", min: 0, max: 2018 },
];

const sortOptions = [
  { value: "time-desc", label: "上映时间（最新优先）" },
  { value: "time-asc", label: "上映时间（最早优先）" },
  { value: "title-asc", label: "作品名（A → Z）" },
  { value: "title-desc", label: "作品名（Z → A）" },
];

const PAGE_SIZE = 8;

export default function WorksList() {
  useSeo({
    title: "希岛爱理影视作品大全 - 类型筛选与年限查询",
    description:
      "希岛爱理影视作品大全，支持按电影、电视剧、短片类型以及上映年份筛选与排序，查看每部作品的剧情简介、主创与角色信息。",
    keywords: "希岛爱理作品,影视作品列表,电影,电视剧,短片,作品筛选",
  });

  const [type, setType] = useState("全部");
  const [yearRange, setYearRange] = useState(0);
  const [sort, setSort] = useState("time-desc");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    const range = yearRanges[yearRange];
    let list = works.filter((w) => {
      const typeOk = type === "全部" || w.type === type;
      const yearOk = w.year >= range.min && w.year <= range.max;
      return typeOk && yearOk;
    });
    list = [...list].sort((a, b) => {
      if (sort === "time-desc") return b.year - a.year;
      if (sort === "time-asc") return a.year - b.year;
      if (sort === "title-asc") return a.title.localeCompare(b.title, "zh");
      return b.title.localeCompare(a.title, "zh");
    });
    return list;
  }, [type, yearRange, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setType("全部");
    setYearRange(0);
    setSort("time-desc");
    setPage(1);
  };

  const updateType = (v: string) => {
    setType(v);
    setPage(1);
  };

  return (
    <div className="w-full pb-16">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "影视作品" }]} />

      <div className="w-full px-4 md:px-8 pt-6">
        <div className="rounded-lg bg-primary-950 text-background-100 p-6 md:p-10">
          <span className="text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
            Works Archive
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mt-2">
            希岛爱理影视作品大全
          </h1>
          <p className="text-sm text-background-300 mt-3 max-w-3xl leading-relaxed">
            这里收录了希岛爱理参与的影视作品，涵盖电影、电视剧与短片。你可以按类型与上映年份进行筛选，并按时间或作品名排序，快速定位想了解的作品。
          </p>
        </div>
      </div>

      {/* 移动端筛选开关 */}
      <div className="lg:hidden w-full px-4 md:px-8 pt-6">
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-filter-3-line text-accent-600" />
            筛选与排序
          </span>
          <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
      </div>

      <div className={`w-full px-4 md:px-8 pt-4 lg:pt-8 ${filterOpen ? "block" : "hidden lg:block"}`}>
        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">类型</span>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200">
                {["全部", ...workTypes].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => updateType(t)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      type === t
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
              <select
                value={yearRange}
                onChange={(e) => {
                  setYearRange(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none cursor-pointer focus:border-primary-400"
              >
                {yearRanges.map((r, i) => (
                  <option key={r.label} value={i}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap lg:ml-auto">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none cursor-pointer focus:border-primary-400"
              >
                {sortOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 pt-6 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-foreground-600">
          共找到 <span className="font-semibold text-accent-600">{filtered.length}</span> 部作品
        </p>
        {(type !== "全部" || yearRange !== 0 || sort !== "time-desc") && (
          <button
            type="button"
            onClick={resetAll}
            className="text-sm text-primary-700 hover:text-accent-600 transition-colors cursor-pointer inline-flex items-center gap-1"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      <div className="w-full px-4 md:px-8 pt-6">
        {paged.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {paged.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300" />
            <p className="mt-3 text-foreground-700">没有符合当前筛选条件的作品</p>
            <p className="text-sm text-foreground-500 mt-1">试试调整类型或年份范围</p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="w-full px-4 md:px-8 pt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setPage(n)}
              className={`min-w-9 h-9 px-3 rounded-md text-sm transition-colors cursor-pointer ${
                n === currentPage
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}